import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PhotoUploader from "@/components/PhotoUploader";
import { Spinner } from "@/components/RequireAuth";
import {
  Field, useListingForm, canSubmit, BasicsSection, PricingSection, AmenitiesSection, PermitSection, InsuranceSection, TaxLicenseSection,
} from "@/components/ListingFormSections";
import { CancellationPolicySection } from "@/components/CancellationPolicy";
import { AddressSection } from "@/components/AddressSection";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

function saveLabel(busy, id) {
  if (busy) return "Saving…";
  return id ? "Save changes" : "Submit for approval";
}

const HostingRequired = ({ onGo }) => (
  <div className="mx-auto mt-16 max-w-md rounded-3xl border bg-white p-10 text-center" data-testid="editor-hosting-required">
    <p className="mb-4 font-semibold">Turn on hosting first</p>
    <Button className="rounded-full" onClick={onGo}>Go to Hosting</Button>
  </div>
);

export default function ListingEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const form = useListingForm(id);
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const { f, set } = form;

  if (!user.is_host) return <HostingRequired onGo={() => navigate("/host")} />;
  if (!f) return <Spinner />;

  const save = async () => {
    setBusy(true);
    let saved;
    try {
      if (id) ({ data: saved } = await api.put(`/listings/${id}`, form.body()));
      else ({ data: saved } = await api.post("/listings", form.body()));
      toast.success("Saved. Your listing goes live once our team approves the permit.");
      if (saved.tax_zone) {
        const z = saved.tax_zone;
        if (z.status === "verified") toast.success("Tax zone verified from the official map");
        else toast.warning(`Tax zone needs review: ${(z.flags || []).join("; ")}`);
      }
      ["host-listings", "listing", "listings"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      navigate("/host");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8" data-testid="listing-editor">
      <h1 className="mb-8 text-4xl font-bold tracking-tight">{id ? "Edit listing" : "List your place"}</h1>
      <div className="space-y-8 rounded-3xl border bg-white p-6 sm:p-8">
        <Field label="Photos (first one is the cover)"><PhotoUploader value={f.photos} onChange={set("photos")} testid="listing-photos" /></Field>
        <BasicsSection form={form} />
        <AddressSection form={form} />
        <PricingSection form={form} />
        <AmenitiesSection form={form} />
        <Field label="House rules"><Textarea data-testid="listing-rules-input" rows={4} value={f.house_rules} onChange={set("house_rules")} /></Field>
        <CancellationPolicySection form={form} />
        <PermitSection form={form} />
        <TaxLicenseSection form={form} />
        <InsuranceSection form={form} />
        <label className="flex items-center gap-3 text-sm font-medium"><Switch data-testid="listing-active-switch" checked={f.active} onCheckedChange={set("active")} />Show this listing in search</label>
        <div className="flex justify-end gap-3 border-t pt-6">
          <Button variant="ghost" className="rounded-full" onClick={() => navigate("/host")}>Cancel</Button>
          <Button data-testid="listing-save-button" disabled={busy || !canSubmit(f)} onClick={save} className="rounded-full px-8">{saveLabel(busy, id)}</Button>
        </div>
      </div>
    </div>
  );
}
